import { formatCurrencyCompact, formatCurrency } from '../../utils/formatters';
import type { OfferSolution } from '../../types/calculations';

/** Expanded scenario labels for readable summary */
const SCENARIO_SHORT_LABELS: Record<string, string> = {
  'std-clean': 'standard + clean',
  'obbb-clean': 'OBBB + clean',
  'std-1031': 'standard + 1031',
  'obbb-1031': 'OBBB + 1031',
};

interface OfferSpreadBarProps {
  offerSolutions: OfferSolution[];
}

export function OfferSpreadBar({ offerSolutions }: OfferSpreadBarProps) {
  if (!offerSolutions || offerSolutions.length < 2) return null;

  // Find min and max offer prices
  const sorted = [...offerSolutions]
    .filter(s => s.offer_price > 0)
    .sort((a, b) => a.offer_price - b.offer_price);

  if (sorted.length < 2) return null;

  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const spread = max.offer_price - min.offer_price;

  // Tax strategy value as percentage of min price
  const taxStrategyPct = min.offer_price > 0
    ? (spread / min.offer_price) * 100
    : 0;

  if (spread <= 0) return null;

  const minLabel = SCENARIO_SHORT_LABELS[min.scenario] ?? min.scenario;
  const maxLabel = SCENARIO_SHORT_LABELS[max.scenario] ?? max.scenario;

  return (
    <div className="flex items-start gap-4 rounded-lg border-l-4 border-l-gold border border-slate-dark bg-charcoal/40 px-5 py-4">
      {/* Spread amount */}
      <div className="shrink-0">
        <div className="text-lg font-mono font-bold text-gold">
          {formatCurrencyCompact(spread)} SPREAD
        </div>
      </div>

      {/* Range description */}
      <div className="text-sm text-stone-700 leading-relaxed">
        Your max offer ranges from{' '}
        <span className="font-mono text-ivory">{formatCurrency(min.offer_price)}</span>{' '}
        <span className="text-stone-700">({minLabel})</span>{' '}
        to{' '}
        <span className="font-mono text-ivory">{formatCurrency(max.offer_price)}</span>{' '}
        <span className="text-stone-700">({maxLabel})</span>.{' '}
        <span className="text-gold font-medium">
          Tax strategy is worth {taxStrategyPct.toFixed(1)}% of purchase price
        </span>
      </div>
    </div>
  );
}
